import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { personName } from '../relationships/relationshipApi.ts';
import { FollowUpModal } from '../renewals/FollowUpModal.tsx';
import { RenewalPath } from '../renewals/RenewalPath.tsx';
import {
  coverageLabel,
  fetchUpcomingRenewals,
  formatRenewalDate,
  renewalHeadline,
  type UpcomingRenewals
} from '../renewals/renewalApi.ts';

const WINDOW = 30;
const SHOWN = 8;

// The next thirty days of the renewal calendar. Hidden when nothing is due or
// the load fails, like the panels around it.
export const RenewalsPanel = ({ onFollowUpLogged }: { onFollowUpLogged?: () => void }) => {
  const [data, setData] = useState<UpcomingRenewals | null>(null);
  const [followUpContactId, setFollowUpContactId] = useState<number | null>(null);

  useEffect(() => {
    fetchUpcomingRenewals(WINDOW, false)
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data?.rows.length) return null;
  const { rows } = data;

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Typography variant="h6">Renewing in the next {WINDOW} days</Typography>
          <MuiLink component={RouterLink} to="/renewals" underline="hover" variant="caption">
            View all
          </MuiLink>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {renewalHeadline(data, WINDOW)}
        </Typography>
        <Stack divider={<Divider />}>
          {rows.slice(0, SHOWN).map((row) => (
            <Box
              key={row.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, flexWrap: 'wrap' }}
            >
              <Typography
                variant="body2"
                sx={{
                  width: 56,
                  flexShrink: 0,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {formatRenewalDate(row.renewalDate, false)}
              </Typography>
              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  <MuiLink
                    component={RouterLink}
                    to={`/contacts/${row.contact.id}/show`}
                    underline="hover"
                    sx={{ fontWeight: 600 }}
                  >
                    {personName(row.contact)}
                  </MuiLink>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {[row.contact.company?.name, coverageLabel(row.coverage)]
                      .filter(Boolean)
                      .map((part) => ` · ${part}`)
                      .join('')}
                  </Typography>
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <RenewalPath path={row.bestPath} />
              </Box>
              <Box sx={{ width: { sm: 120 }, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFollowUpContactId(row.contact.id)}
                >
                  Log follow-up
                </Button>
              </Box>
            </Box>
          ))}
        </Stack>
        {rows.length > SHOWN ? (
          <Typography variant="caption" color="text.secondary">
            Showing {SHOWN} of {rows.length}.{' '}
            <MuiLink component={RouterLink} to={`/renewals?within=${WINDOW}`} underline="hover">
              See them all
            </MuiLink>
          </Typography>
        ) : null}
      </CardContent>
      <FollowUpModal
        contactId={followUpContactId}
        onClose={() => setFollowUpContactId(null)}
        onSaved={onFollowUpLogged}
      />
    </Card>
  );
};
